import { type ReactNode } from 'react';

/* ──────────────────────────────────────────────────────────────
 *  Moteur Markdown partagé de Visaly.
 *  Extrait tel quel de ResultsDisplay.tsx (rendu + export HTML/texte)
 *  pour être réutilisable sans dupliquer la logique. Comportement
 *  strictement identique à l'original.
 * ────────────────────────────────────────────────────────────── */

/* ── Markdown pre-processing ────────────────────────────── */

export function fixMarkdownFormatting(md: string): string {
  return md
    // Emoji bullets inline → new line
    .replace(/([.!?:;»]) *([✅❌⚠️☑☒☐✔✖●○])/g, '$1\n\n$2')
    // Numbered items inline "1) " → new line
    .replace(/([.!?:;»]) +(\d+\)) /g, '$1\n\n$2 ')
    // List items "- " inline → new line (when after punctuation and followed by uppercase, bold, or italic)
    .replace(/([.!?:;»]) +- (?=[A-ZÀ-ÖØ-Ý*])/g, '$1\n\n- ')
    // "=> " arrows inline → new line
    .replace(/([.!?:;»]) +(=>|→) /g, '$1\n\n$2 ');
}

/** Parse bold/italic inline via regex.
 *  boldClass controls the color of bold text (default: inherit from parent). */
export function parseInline(text: string, boldClass = 'font-semibold text-slate-900'): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={key++} className={boldClass}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(<em key={key++}>{match[2]}</em>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

/** Render markdown text with line breaks and list support.
 *  inheritBold: if true, bold inherits the parent color instead of forcing dark. */
export function Md({ children, inheritBold }: { children: string; inheritBold?: boolean }) {
  const text = fixMarkdownFormatting(children);
  const lines = text.split('\n').filter(l => l.trim() !== '');
  const boldCls = inheritBold ? 'font-semibold' : 'font-semibold text-slate-900';

  if (lines.length <= 1) {
    return <span className="break-words">{parseInline(text.trim(), boldCls)}</span>;
  }

  return (
    <div className="break-words space-y-1.5">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('- ') || trimmed.match(/^\d+[\.\)]\s/)) {
          const content = trimmed.replace(/^-\s+/, '').replace(/^\d+[\.\)]\s+/, '');
          return (
            <div key={i} className="flex gap-2">
              <span className="shrink-0 mt-0.5 opacity-40">•</span>
              <span>{parseInline(content, boldCls)}</span>
            </div>
          );
        }
        if (trimmed.startsWith('=>') || trimmed.startsWith('→')) {
          return <p key={i} className="font-semibold mt-1">{parseInline(trimmed, boldCls)}</p>;
        }
        return <p key={i}>{parseInline(trimmed, boldCls)}</p>;
      })}
    </div>
  );
}

/** Block-level markdown for long texts (arbitrage, summary).
 *  Splits into paragraphs, renders headings, lists, bold/italic, and tables. */
export function MdBlock({ children, className = '' }: { children: string; className?: string }) {
  const text = fixMarkdownFormatting(children);
  const lines = text.split('\n');
  const elements: ReactNode[] = [];
  let key = 0;
  let tableRows: string[][] = [];
  let tableHeaders: string[] = [];
  let inTable = false;

  const flushTable = () => {
    if (tableHeaders.length === 0) return;
    elements.push(
      <div key={key++} className="overflow-x-auto my-4">
        <table className="w-full border-collapse text-[0.8em]">
          <thead>
            <tr>
              {tableHeaders.map((h, i) => (
                <th key={i} className="text-left font-semibold p-2 border-b-2 border-slate-200 text-slate-900">
                  {parseInline(h.trim())}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="p-2 border-b border-slate-100 align-top leading-relaxed break-words">
                    {parseInline(cell.trim())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    tableHeaders = [];
    tableRows = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Table detection
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.slice(1, -1).split('|');
      // Separator row (|---|---|)
      if (cells.every(c => /^[\s:-]+$/.test(c))) {
        inTable = true;
        continue;
      }
      if (!inTable && tableHeaders.length === 0) {
        tableHeaders = cells;
        continue;
      }
      if (inTable) {
        tableRows.push(cells);
        continue;
      }
    } else if (inTable) {
      flushTable();
    }

    // Empty line
    if (trimmed === '') {
      elements.push(<div key={key++} className="h-3" />);
      continue;
    }
    // Horizontal rule
    if (trimmed === '---' || trimmed === '***') {
      elements.push(<hr key={key++} className="border-none border-t border-slate-200 my-6" />);
      continue;
    }
    // Headings
    const hMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const sizes = ['text-xl', 'text-lg', 'text-base', 'text-sm'];
      const margins = ['mt-8 mb-3', 'mt-7 mb-3', 'mt-6 mb-2', 'mt-5 mb-2'];
      elements.push(
        <p key={key++} className={`font-display ${sizes[level - 1]} ${margins[level - 1]} text-slate-900`}>
          {parseInline(hMatch[2])}
        </p>,
      );
      continue;
    }
    // List items
    if (trimmed.startsWith('- ') || trimmed.match(/^\d+[\.\)]\s/)) {
      const content = trimmed.replace(/^-\s+/, '').replace(/^\d+[\.\)]\s+/, '');
      const indent = line.match(/^(\s*)/)?.[1].length ?? 0;
      elements.push(
        <div key={key++} className={`flex gap-2 ${indent > 2 ? 'ml-5' : ''} my-1`}>
          <span className="text-slate-400 shrink-0 mt-0.5">•</span>
          <span className="break-words">{parseInline(content)}</span>
        </div>,
      );
      continue;
    }
    // Regular paragraph
    elements.push(<p key={key++} className="my-2 break-words">{parseInline(trimmed)}</p>);
  }
  flushTable();

  return <div className={`leading-relaxed ${className}`}>{elements}</div>;
}

/* ── Markdown → HTML / plain for sharing ────────────────── */

export function markdownToHtml(md: string): string {
  const thStyle = 'text-align:left;font-weight:bold;padding:6px 10px;border-bottom:2px solid #e2e8f0;';
  const tdStyle = 'text-align:left;padding:6px 10px;border-bottom:1px solid #f1f5f9;vertical-align:top;';

  // Step 1: Escape HTML, then extract tables into placeholders
  let src = md.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const tables: string[] = [];
  const srcLines = src.split('\n');
  const out: string[] = [];
  let j = 0;

  while (j < srcLines.length) {
    const ln = srcLines[j].trim();
    if (ln.startsWith('|') && ln.endsWith('|') && j + 1 < srcLines.length) {
      const sep = srcLines[j + 1]?.trim() ?? '';
      if (sep.startsWith('|') && /^[\s|:-]+$/.test(sep)) {
        const hdrs = ln.slice(1, -1).split('|').map(c => c.trim());
        let t = `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><thead><tr>`;
        hdrs.forEach(h => { t += `<th style="${thStyle}">${applyInlineHtml(h)}</th>`; });
        t += '</tr></thead><tbody>';
        j += 2;
        while (j < srcLines.length) {
          const rl = srcLines[j].trim();
          if (!rl.startsWith('|') || !rl.endsWith('|')) break;
          const cells = rl.slice(1, -1).split('|').map(c => c.trim());
          t += '<tr>';
          cells.forEach(c => { t += `<td style="${tdStyle}">${applyInlineHtml(c)}</td>`; });
          t += '</tr>';
          j++;
        }
        t += '</tbody></table>';
        const placeholder = `__TABLE_${tables.length}__`;
        tables.push(t);
        out.push(placeholder);
        continue;
      }
    }
    out.push(srcLines[j]);
    j++;
  }

  // Step 2: Apply markdown formatting on non-table text
  let html = out.join('\n')
    .replace(/^#### (.+)$/gm, '<h4 style="font-size:15px;margin:18px 0 6px;">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 style="font-size:17px;margin:20px 0 8px;">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 style="font-size:19px;margin:22px 0 8px;">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 style="font-size:22px;margin:24px 0 10px;">$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e2e8f0;margin:20px 0;">')
    .replace(/^- (.+)$/gm, '<li style="margin:4px 0;">$1</li>')
    .replace(/\n\n/g, '</p><p style="margin:10px 0;">')
    .replace(/\n/g, '<br>');
  html = html.replace(/(<li[^>]*>.*?<\/li>(?:<br>)?)+/g, (m) => '<ul style="padding-left:20px;margin:10px 0;">' + m.replace(/<br>/g, '') + '</ul>');

  // Step 3: Re-inject tables
  tables.forEach((t, i) => { html = html.replace(`__TABLE_${i}__`, t); });

  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#334155;"><p style="margin:10px 0;">${html}</p></div>`;
}

export function applyInlineHtml(text: string): string {
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
}

export function markdownToPlainText(md: string): string {
  return md
    .replace(/^#{1,4}\s+/gm, '')
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/^---$/gm, '————')
    .replace(/^- /gm, '  - ');
}

/** Copy rich HTML + plain text to clipboard */
export async function copyRich(md: string) {
  const html = markdownToHtml(md);
  const plain = markdownToPlainText(md);
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ]);
  } catch {
    await navigator.clipboard.writeText(plain);
  }
}
